import { useMemo } from "react"
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    DoughnutController,
    type Plugin as ChartPlugin,
} from "chart.js"
import { Tooltip } from "@mui/material"
import { Doughnut } from "react-chartjs-2"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { DashboardDataCard, DashboardDataContents } from "../../../../../dashboards/components/DashboardDataCard"
import { CheckinSummary } from "../../merchantHomeTypes"
import { MerchantHomeQuery, fetchCheckInSummary } from "../../merchantHomeApi"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { Box } from "@mui/system"

ChartJS.register(DoughnutController, ArcElement, ChartTooltip, Legend)

type ChartProps = Readonly<{
    summary: CheckinSummary
}>

export function CheckInChart({ summary }: ChartProps) {
    const { checkInsPercentage, noCheckInsPercentage, numberOfCheckIns, numberOfOrders } = summary

    const { l10n } = useAppLocalization()

    const plugins = useMemo<ChartPlugin<"doughnut">[]>(
        () => [
            {
                id: "center-text",
                afterDraw(chart, _args, _options) {
                    const width = chart.chartArea.width
                    const height = chart.chartArea.height
                    const ctx = chart.ctx

                    ctx.restore()
                    ctx.font = `normal 500 1.25rem Roboto`
                    ctx.textBaseline = "top"

                    const text = `${checkInsPercentage}%`
                    const textDims = ctx.measureText(text)
                    const textHeight = textDims.actualBoundingBoxAscent + textDims.actualBoundingBoxDescent

                    const textX = Math.round((width - textDims.width) / 2)
                    const textY = Math.round((height - textHeight) / 2)

                    ctx.fillText(text, textX, textY)
                    ctx.save()
                },
            },
        ],
        [checkInsPercentage],
    )

    const data = useMemo(() => {
        return {
            labels: [
                l10n.getString("merchant-home-checkin-card-participants", { checkedIn: numberOfCheckIns }),
                l10n.getString("merchant-home-checkin-card-none-participants", {
                    notCheckedIn: numberOfOrders - numberOfCheckIns,
                }),
            ],
            datasets: [
                {
                    label: l10n.getString("merchant-home-checkin-card-participation-percentage"),
                    data: [checkInsPercentage, noCheckInsPercentage],
                    backgroundColor: ["#22C55E", "#FCA5A5"],
                    borderColor: ["#22C55E", "#FCA5A5"],
                    borderWidth: 1,
                },
            ],
        }
    }, [l10n, numberOfCheckIns, numberOfOrders, checkInsPercentage, noCheckInsPercentage])

    return (
        <Doughnut
            data={data}
            options={{
                responsive: true,
                maintainAspectRatio: true,
                cutout: "65%",
                plugins: {
                    legend: {
                        position: "bottom",
                        labels: {
                            boxWidth: 7,
                            boxHeight: 7,
                            usePointStyle: true,
                            useBorderRadius: true,
                            pointStyle: "circle",
                            textAlign: "center",
                        },
                    },
                },
            }}
            plugins={plugins}
        />
    )
}

type DataLoaderProps = Readonly<{
    periodInDays: number
}>

function DataLoader({ periodInDays }: DataLoaderProps) {
    const { merchantId } = useMerchant()

    const checkInSummary = useApiQuery(fetchCheckInSummary, {
        queryName: MerchantHomeQuery.STATISTICS_CHECK_IN_SUMMARY,
        dependencies: {
            merchantId,
            periodInDays,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    return <>{checkInSummary.data && <CheckInChart summary={checkInSummary.data.checkInSummary} />}</>
}

type PieChartCheckInsProps = Readonly<{
    title?: string
    periodInDays: number
}>

export function StatisticsPieChartCheckIns({ title, periodInDays }: PieChartCheckInsProps) {
    return (
        <LocalizedStrict id="merchant-home-statistics-check-in-card-tooltip" attrs={{ title: true }}>
            <Tooltip title="Shows how many customers remember to scan their proof of purchase on the check-in screen versus how many forget to do it">
                <Box>
                    <LocalizedStrict id="merchant-home-checkin-card" attrs={{ title: !title }}>
                        <DashboardDataCard title={title} isHeighNormal={false}>
                            <DashboardDataContents>
                                <DataLoader periodInDays={periodInDays} />
                            </DashboardDataContents>
                        </DashboardDataCard>
                    </LocalizedStrict>
                </Box>
            </Tooltip>
        </LocalizedStrict>
    )
}
